import { ref, readonly } from 'vue'

/**
 * Toast notification types
 */
export const TOAST_TYPES = Object.freeze({
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
})

/**
 * Default durations per type (in ms)
 */
const DEFAULT_DURATIONS = {
  [TOAST_TYPES.SUCCESS]: 4000,
  [TOAST_TYPES.ERROR]: 6000,
  [TOAST_TYPES.WARNING]: 5000,
  [TOAST_TYPES.INFO]: 4000
}

// Global toast state (singleton pattern)
const toasts = ref([])
let toastId = 0

/**
 * Composable for managing toast notifications
 * Replaces alert() with a modern, accessible notification system
 */
export function useToast() {
  /**
   * Add a new toast notification
   * @param {Object} options - Toast options
   * @param {string} options.message - The message to display
   * @param {string} [options.type='info'] - Toast type (success, error, warning, info)
   * @param {string} [options.title] - Optional title
   * @param {number} [options.duration] - Duration in ms (0 for persistent)
   * @param {boolean} [options.dismissible=true] - Can be dismissed by user
   * @returns {number} - Toast ID for manual dismissal
   */
  function addToast({
    message,
    type = TOAST_TYPES.INFO,
    title = null,
    duration = null,
    dismissible = true
  }) {
    const id = ++toastId

    const toast = {
      id,
      message,
      type,
      title,
      dismissible,
      createdAt: Date.now()
    }

    toasts.value.push(toast)

    // Auto-dismiss after duration
    const actualDuration = duration ?? DEFAULT_DURATIONS[type]
    if (actualDuration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, actualDuration)
    }

    return id
  }

  /**
   * Remove a toast by ID
   * @param {number} id - Toast ID
   */
  function removeToast(id) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  /**
   * Clear all toasts
   */
  function clearAllToasts() {
    toasts.value = []
  }

  // Convenience methods
  function success(message, options = {}) {
    return addToast({ message, type: TOAST_TYPES.SUCCESS, ...options })
  }

  function error(message, options = {}) {
    return addToast({ message, type: TOAST_TYPES.ERROR, ...options })
  }

  function warning(message, options = {}) {
    return addToast({ message, type: TOAST_TYPES.WARNING, ...options })
  }

  function info(message, options = {}) {
    return addToast({ message, type: TOAST_TYPES.INFO, ...options })
  }

  return {
    // State (readonly to prevent external mutation)
    toasts: readonly(toasts),

    // Methods
    addToast,
    removeToast,
    clearAllToasts,

    // Convenience methods
    success,
    error,
    warning,
    info
  }
}

// Export singleton instance for global use
export const toast = useToast()
